import { BreakpointObserver } from '@angular/cdk/layout';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { Component, inject, OnDestroy, OnInit, PLATFORM_ID, Renderer2, signal } from '@angular/core';
import { SafeHtml } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { APP_CONFIG } from '@core-app/app.config';
import {
    ApiService,
    AuthService,
    BrandingService,
    DocumentService,
    ImageService,
    PageService,
    PageType,
    PostService,
    PostType,
    SettingsService,
    TranslateService,
    WindowService,
    ZoneService,
} from '@gallaghercp/core-frontend';
import { TranslateService as TranslateProvider } from '@ngx-translate/core';
import { Observable, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import { ClientService } from '../../services/client.service';

@Component({
    selector: 'core-header',
    templateUrl: './header.component.html',
    standalone: false,
    host: {
        '(window:resize)': 'onResizeHeader($event)',
        '(window:scroll)': 'onScroll()',
    },
})
export class HeaderComponent implements OnInit, OnDestroy {
    protected auth = inject(AuthService);
    protected pages = inject(PageService);
    protected posts = inject(PostService);
    client = inject(ClientService);
    settings = inject(SettingsService);
    protected breakpoint = inject(BreakpointObserver);
    protected router = inject(Router);
    protected api = inject(ApiService);
    protected imageService = inject(ImageService);
    protected windowService = inject(WindowService);
    protected documentService = inject(DocumentService);
    private platformId = inject(PLATFORM_ID);
    protected uiBranding = inject(BrandingService);
    protected zoneService = inject(ZoneService);
    protected renderer = inject(Renderer2);
    private document = inject(DOCUMENT);

    user: any;
    isLoggedIn: boolean = false;

    pages$?: Observable<any>;
    page?: PageType;
    post?: PostType;

    openChildNav: PageType | null = null;
    hamburger = false;
    hamburgerChildNav = false;
    authenticationType?: string;
    isMobileNav = false;
    isBrowser!: boolean;
    isDesktopHeader = signal<boolean>(true);

    isTopNav$?: Observable<boolean>;
    isTransparentNav?: boolean;
    isTopNavTransparentSticky?: boolean;
    isIndividualMenuFlyout?: boolean = false;
    menuFlyoutPageId: number | undefined;
    resizeTimeoutHeader: any;

    subscriptions: Subscription[] = [];

    // Config variables
    darkMode?: boolean;
    postsInNavEnabled?: boolean;
    postsEnabled?: boolean;
    notificationsEnabled?: boolean;
    showDarkModeSwitch?: boolean;
    searchEnabled?: boolean;
    heroImagesEnabled?: boolean;
    isCollapsed = true;
    isDemoMode = false;

    isHeaderMinimised?: boolean;

    translateProvider: TranslateProvider = inject(TranslateProvider);

    private translate: TranslateService = inject(TranslateService);

    isZoneRouteOne = (APP_CONFIG as any).splashLandingPage
        ? (APP_CONFIG as any).splashLandingPage['isZoneRouteOne'] !== undefined
            ? (APP_CONFIG as any).splashLandingPage['isZoneRouteOne']
            : true
        : false;

    navBreakpointHeader = (APP_CONFIG as any).navigation?.breakpoint || 1088;
    isInlineLogoAndNav: boolean = !!(APP_CONFIG as any).theme?.navigation?.inlineLogoAndNav;
    showUtilitiesAbove: boolean = !!(APP_CONFIG as any).theme?.navigation?.showUtilitiesAbove;
    showUtilitiesBelow: boolean = !!(APP_CONFIG as any).theme?.navigation?.showUtilitiesBelow;

    get canShowUtilitiesAbove(): boolean {
        if (!this.isInlineLogoAndNav && this.isTransparentNav) {
            return false;
        }
        return this.showUtilitiesAbove;
    }

    get canShowUtilitiesBelow(): boolean {
        if (!this.isInlineLogoAndNav && this.isTransparentNav) {
            return false;
        }
        return this.showUtilitiesBelow;
    }

    ngOnInit(): void {
        this.isBrowser = isPlatformBrowser(this.platformId);
        this.isTopNav$ = this.settings.isTopNavigation();

        if (this.isBrowser) {
            this.isDesktopCheckHeader(this.windowService.nativeWindow);
        }

        if (this.isBrowser && this.isInlineLogoAndNav) {
            this.renderer.addClass(this.document.body, 'nav-inline');
            setTimeout(() => {
                this.renderer.removeClass(this.document.body, 'nav-function-transparent');
                this.renderer.addClass(this.document.body, 'nav-function-solid');
            }, 1000);
        }

        if (this.isBrowser && this.showUtilitiesAbove) {
            this.renderer.addClass(this.document.body, 'nav-utility-above');
        }

        if (this.isBrowser && this.showUtilitiesBelow) {
            this.renderer.addClass(this.document.body, 'nav-utility-below');
        }

        if (APP_CONFIG.demoMode !== undefined) {
            this.isDemoMode = APP_CONFIG.demoMode;
        }

        this.resetChildNav();
        this.hamburger = false;
        this.hamburgerChildNav = false;

        this.subscriptions.push(
            this.settings.isTransparentNavigation().subscribe((state: any) => {
                this.isTransparentNav = this.isInlineLogoAndNav ? false : state;
                this.updateLogoUrl();
            }),
            this.settings.showPosts().subscribe((state: any) => {
                this.postsEnabled = state;
            }),
            this.settings.showNotifications().subscribe((state: any) => {
                // Respect root config override. If APP_CONFIG.notificationsEnabled === false,
                // always hide notifications regardless of service state. Otherwise use service state.
                const cfg = (APP_CONFIG as any)?.notificationsEnabled;
                this.notificationsEnabled = cfg === false ? false : !!state;
            }),
            this.settings.showSearch().subscribe((state: any) => {
                this.searchEnabled = state;
            }),
            this.settings.showHeroImages().subscribe((state: any) => {
                this.heroImagesEnabled = state;
                this.updateLogoUrl();
            }),
            this.settings.showArticleThumbnails().subscribe((state: any) => {
                this.postsInNavEnabled = state;
            }),
            this.settings.isDarkMode().subscribe((state: any) => {
                this.darkMode = state;
                this.updateLogoUrl();
            }),
            this.settings.isDarkModeEnabled().subscribe((state: any) => {
                if (state === false && this.darkMode) {
                    this.settings.toggle('isDarkMode');
                }

                // Hidden when the root config explicitly disables it, otherwise driven by service state.
                this.showDarkModeSwitch = (APP_CONFIG as any)?.showDarkModeSwitch !== false && !!state;

                this.updateLogoUrl();
            }),
            this.pages.currentPage$!.subscribe((page: PageType) => {
                this.page = page;
                this.post = undefined;
                this.updateLogoUrl();
            }),
            this.posts.currentPost$!.subscribe((post: PostType) => {
                this.post = post;
                this.page = undefined;
                this.updateLogoUrl();
            }),
            this.breakpoint.observe(['(max-width: 991px)']).subscribe(result => {
                this.isMobileNav = result.matches;
            }),
            this.api.zoneChangeEmit.subscribe((zone: number | false) => {
                this.getPages(zone);
            }),
            this.translateProvider.onLangChange.subscribe(() => {
                this.getPages();
            }),
            this.client.getAccessType().subscribe((type: any) => {
                this.authenticationType = type;
            }),
            this.auth.authChangeEmit.subscribe(() => {
                this.user = this.auth.getUser();
                this.isLoggedIn = this.auth.isAuthenticated();
            }),
        );
    }

    showHeader(): boolean {
        return (
            this.auth.isAuthenticated() || this.authenticationType === 'public' || this.authenticationType === 'mixed'
        );
    }

    trackByFn(idx: number, _t46: any) {
        return idx;
    }

    childNav(page: any) {
        this.openChildNav = page;
    }

    resetChildNav() {
        this.openChildNav = null;
    }

    logout() {
        this.api.setServiceZone(1);
        this.router.navigate(['/logout']);
    }

    sortChildren(children: PageType[]) {
        children.sort((a, b) => (a.name > b.name ? 1 : -1));
    }

    isCurrentPage(page: any) {
        return this.pages.getCurrentPage().url === page.url;
    }

    minimiseHeader(isScrollActive: boolean) {
        this.isHeaderMinimised = isScrollActive;
    }

    onScroll() {
        const offset = 100;
        const currentPosition = this.windowService.pageYOffset;
        if (currentPosition > offset) {
            this.minimiseHeader(true);
        } else {
            this.minimiseHeader(false);
        }
        if (this.isTransparentNav) {
            this.isTopNavTransparentSticky = currentPosition > offset;
            this.updateLogoUrl();
        }
    }

    showInMenu(page: any, type: string) {
        if (
            page.meta?.showInUtilitiesNavbarAbove == 'true' ||
            page.meta?.showInUtilitiesNavbarAbove == true ||
            page.meta?.showInUtilitiesNavbarBelow == 'true' ||
            page.meta?.showInUtilitiesNavbarBelow == true
        ) {
            return false;
        }

        if (type === 'no-child') {
            return (
                page.meta?.showInMenu == 'true' || page.meta?.showInMenu == "'true'" || page.meta?.showInMenu == true
            );
        } else if (type === 'has-child') {
            return (
                (page.meta?.showInMenu == 'true' ||
                    page.meta?.showInMenu == "'true'" ||
                    page.meta?.showInMenu == true) &&
                !page.meta?.hideNavDropdown
            );
        } else {
            return false;
        }
    }

    isDemoModeAndPostsLink(page: any) {
        return this.isDemoMode && page.url === 'articles';
    }

    getSiteName() {
        return this.client.getSiteName();
    }

    updateLogoUrl(): void {
        // No-op in base class; subclasses or BrandLogoComponent handle their own logo selection
    }

    // Styles are now applied via HostBinding during initial render to avoid flicker

    ngOnDestroy() {
        this.subscriptions.forEach(subscription => {
            subscription.unsubscribe();
        });
    }

    goToMain(elementId: string) {
        const element = this.documentService.getElementById(elementId);
        element?.focus();
    }

    /**
     * Retrieves a valid image URL based on the provided page metadata.
     *
     * @param {any} page - Page object used to determine the valid image URL.
     * @return {string} - A valid image URL as a string.
     */
    validImageUrl(page: any): SafeHtml {
        return this.imageService.getImageUrl(page, 'menu');
    }

    private getPages(zone?: number | false): void {
        const headers: any = zone ? { 'x-service-zone': String(zone) } : {};
        const currentPage: PageType = this.pages.getCurrentPage();

        this.pages$ = this.pages.getPages(headers).pipe(
            map((pages: PageType[]) => {
                return pages.map((page: PageType) => {
                    this.sortChildren(page.children);

                    // Open child navigation if conditions are met
                    if (
                        !this.openChildNav &&
                        currentPage &&
                        (currentPage?.parentId ?? 0) > 0 &&
                        page.id === currentPage.parentId
                    ) {
                        this.openChildNav = page;
                    }

                    // Translate common properties and assign them
                    const translatedProperties = ['name', 'description', 'url', 'tags'].reduce((acc, key) => {
                        acc[key] = this.translate.getPageSpecific(page, key);
                        return acc;
                    }, {} as any);

                    // Translate and map children
                    page.children = page.children.map((child: PageType) => ({
                        ...child,
                        name: this.translate.getPageSpecific(child, 'name'),
                        description: this.translate.getPageSpecific(child, 'description'),
                        url: this.zoneService.injectZoneCodeUrl(this.translate.getPageSpecific(child, 'url')),
                        tags: this.translate.getPageSpecific(child, 'tags'),
                    }));

                    // Checks for true 'isFlyoutMenu' meta value on each page and sets a boolean and a page id to a couple of vars. This is used to allow individual flyout menus on pages if required.
                    if (page.meta && page.meta?.['isFlyoutMenu']) {
                        if (page.meta?.['isFlyoutMenu'] === 'true') {
                            this.isIndividualMenuFlyout = true;
                            this.menuFlyoutPageId = page.id;
                        }
                    }

                    return { ...page, ...translatedProperties };
                });
            }),
        );
    }

    navigateToHome() {
        // Only set the zone if we're using a splash landing page with home zone route set to 1 via config
        if (this.isZoneRouteOne) {
            this.api.setServiceZone(1);
        }
        this.router.navigate(['/']);
    }

    onResizeHeader(event: Event) {
        if (!this.isBrowser) return;

        clearTimeout(this.resizeTimeoutHeader);
        this.resizeTimeoutHeader = setTimeout(() => {
            this.isDesktopCheckHeader(event.target);
        }, 200);
    }

    isDesktopCheckHeader(elem: any) {
        if (elem.innerWidth >= this.navBreakpointHeader) {
            if (!this.isCollapsed) {
                this.isCollapsed = true;
            }
            this.isDesktopHeader.set(true);
        } else {
            this.isDesktopHeader.set(false);
        }
    }
}
